<!-- PDF preview overlay -->
<div class="preview-overlay" *ngIf="showPreview">
  <div class="preview-container">
    <div class="preview-header">
      <h3>PRN Preview</h3>
      <div class="d-flex gap-2">
        <button class="btn btn-secondary" (click)="printPdf()"><i class="bi bi-printer"></i> Print</button>
        <button class="btn btn-secondary" (click)="downloadPdf()"><i class="bi bi-download"></i> Download</button>
        <button class="btn btnprimary" (click)="closePreview()">Done</button>
      </div>
    </div>
    <div class="preview-body">
      <div *ngIf="isLoadingPreview" class="loading-spinner">Loading PDF...</div>
      <embed *ngIf="previewUrl" [src]="previewUrl" type="application/pdf" class="pdf-viewer">
    </div>
  </div>
</div>

<div [class.blurred]="showPreview">
  <div class="content-info prn-page">

    <!-- ── Sticky document header ─────────────────────────────────────── -->
    <div class="zf-doc-head">
      <div class="zf-doc-left">
        <a [routerLink]="['/prn']" class="zf-doc-back"><i class="bi bi-arrow-left"></i> Purchase Return Notes</a>
        <div class="zf-doc-title-row">
          <h3 class="zf-doc-title">{{ pageTitle }}</h3>
          @if (prnNumber) {
          <span class="zf-doc-no">{{ prnNumber }}</span>
          }
          @if (viewOnly) {
          <span class="zf-status dot">Read only</span>
          } @else if (isEdit) {
          <span class="zf-status info dot">Editing</span>
          } @else {
          <span class="zf-status warn dot">Draft</span>
          }
        </div>
      </div>

      <!-- Bulk entry via Excel (same flow as Purchase Order) + the document actions -->
      <div class="zf-doc-actions" *ngIf="!viewOnly">
        <!-- Template is a blank S.No / SKU / Qty sheet — no vendor needed to
             download it; the vendor is validated when the file is uploaded. -->
        <button type="button" class="btn btn-secondary text-nowrap" (click)="downloadTemplate()"
          title="Download a blank template (S.No, SKU, Qty)">
          <i class="bi bi-download"></i> Template
        </button>
        <label class="btn btn-secondary text-nowrap mb-0" [class.disabled]="!selectedVendorId"
          title="{{ selectedVendorId ? 'Upload the filled template' : 'Select a vendor first' }}">
          <i class="bi bi-upload"></i> Upload items
          <input type="file" accept=".xlsx,.xls" hidden [disabled]="!selectedVendorId"
            (change)="onTemplateFileSelected($event)" />
        </label>
        @if (isUploading) {
        <span class="text-muted small"><i class="bi bi-hourglass-split me-1"></i>Uploading...</span>
        }
        <span class="zf-vsep"></span>
        <button type="button" class="btn btn-secondary" (click)="saveAsDraft()" [disabled]="!prnItems.length">
          Save draft
        </button>
        <button type="button" class="btn zf-btn-hero" (click)="submitPrn()" [disabled]="!prnItems.length">
          {{ isEdit ? 'Update PRN' : 'Submit PRN' }}
        </button>
      </div>
    </div>

    <!-- ── Return header ──────────────────────────────────────────────── -->
    <div class="zf-doc-section">
      <div class="zf-section-head collapsible" [class.closed]="!headerOpen" (click)="headerOpen = !headerOpen">
        <span class="zf-section-title">Return header</span>
        <div class="zf-sum-chips">
          <span class="zf-sum-chip prn-store-chip">
            <span class="k">Store</span>
            <app-warehouse-select (selectionChange)="onWarehouseChange($event)"></app-warehouse-select>
          </span>
          <span class="zf-sum-chip" [class.muted]="!selectedVendorName">
            <span class="k">Vendor</span>{{ selectedVendorName || 'Not selected' }}
          </span>
          <span class="zf-sum-chip" [class.muted]="!invoiceNumber">
            <span class="k">Invoice</span><span class="mono">{{ invoiceNumber || '—' }}</span>
          </span>
          <span class="zf-sum-chip" [class.muted]="!returnDate">
            <span class="k">Return date</span>
            <span class="mono">{{ (returnDate | date:'dd MMM yyyy') || 'Not set' }}</span>
          </span>
          @if (prnItems.length) {
          <span class="zf-sum-chip primary"><span class="k">Net</span>₹{{ prnSummary.finalTotal | number:'1.2-2' }}</span>
          }
        </div>
        <span class="zf-section-toggle">
          {{ headerOpen ? 'Collapse header' : 'Edit header' }}
          <i class="bi" [class.bi-chevron-up]="headerOpen" [class.bi-chevron-down]="!headerOpen"></i>
        </span>
      </div>

      @if (headerOpen) {
      <div class="zf-section-body zf-stack">

        <div class="zf-fgrid" style="--cols: minmax(0,1.4fr) repeat(4, minmax(0,1fr))">
          <div class="zf-field">
            <span class="zf-flabel">Select vendor <span class="req">*</span></span>
            <app-search-select [items]="vendors" bindLabel="name" bindValue="id" entity="vendor"
              [(ngModel)]="selectedVendorId" placeholder="Search vendor" (ngModelChange)="onVendorSelected($event)"
              (search)="onVendorSearch($event)" [loading]="isVendorLoading" [disabled]="viewOnly">
            </app-search-select>
          </div>

          <div class="zf-field">
            <span class="zf-flabel">PRN no <span class="meta">· auto</span></span>
            <input type="text" class="form-control zf-mono-input" readonly [(ngModel)]="prnNumber" />
          </div>

          <div class="zf-field">
            <span class="zf-flabel">Invoice number</span>
            <input type="text" class="form-control zf-mono-input" [(ngModel)]="invoiceNumber" [readOnly]="viewOnly"
              placeholder="Vendor invoice no." />
          </div>

          <div class="zf-field">
            <span class="zf-flabel">Invoice date</span>
            <input type="date" class="form-control" [(ngModel)]="invoiceDate" [readOnly]="viewOnly" />
          </div>

          <div class="zf-field">
            <span class="zf-flabel">Return date</span>
            <input type="date" class="form-control" [(ngModel)]="returnDate" [readOnly]="viewOnly" />
          </div>
        </div>

        <div class="zf-hr"></div>

        <div class="zf-fgrid" style="--cols: repeat(5, minmax(0,1fr))">
          <div class="zf-field">
            <span class="zf-flabel">Freight charges</span>
            <div class="zf-affix prefix" [class.disabled]="viewOnly">
              <span class="zf-affix-k">₹</span>
              <input type="number" [(ngModel)]="freightCharges" (change)="updatePrnSummary()" [readOnly]="viewOnly"
                placeholder="0" />
            </div>
          </div>

          <div class="zf-field">
            <span class="zf-flabel">Packing charges</span>
            <div class="zf-affix prefix" [class.disabled]="viewOnly">
              <span class="zf-affix-k">₹</span>
              <input type="number" [(ngModel)]="packingCharges" (change)="updatePrnSummary()" [readOnly]="viewOnly"
                placeholder="0" />
            </div>
          </div>

          <div class="zf-field">
            <span class="zf-flabel">Other discounts</span>
            <div class="zf-affix prefix" [class.disabled]="viewOnly">
              <span class="zf-affix-k">₹</span>
              <input type="number" [(ngModel)]="otherDiscounts" (ngModelChange)="onOtherDiscountsChange()"
                (change)="updatePrnSummary()" [readOnly]="viewOnly" placeholder="0" />
            </div>
          </div>
        </div>
      </div>
      }
    </div>

    <!-- ── Return lines ───────────────────────────────────────────────── -->
    <div class="zf-doc-section zf-lines-card">
      <div class="zf-lines-head">
        <h2>Return lines</h2>
        <span class="zf-count">{{ prnLineCountLabel }}</span>
        <span class="zf-units">{{ totalReturnedQuantity | number:'1.0-2' }} units</span>
        <div class="zf-pills">
          <button type="button" class="zf-pill" [class.on]="lineFilter === 'all'" (click)="lineFilter = 'all'">
            All<span class="n">{{ prnItems.length }}</span>
          </button>
          <button type="button" class="zf-pill" [class.on]="lineFilter === 'selected'" (click)="lineFilter = 'selected'">
            Selected<span class="n">{{ selectedCount }}</span>
          </button>
          <button type="button" class="zf-pill" [class.on]="lineFilter === 'discounted'" (click)="lineFilter = 'discounted'">
            Discounted<span class="n">{{ discountedCount }}</span>
          </button>
          @if (lineFilter !== 'all' || lineQuery) {
          <button type="button" class="zf-pill clear" (click)="clearLineFilters()">✕ Clear filters</button>
          }
        </div>

        <div class="zf-lines-tools">
          <label class="zf-search" style="width:170px">
            <i class="bi bi-search"></i>
            <input [(ngModel)]="lineQuery" placeholder="Filter these lines" />
          </label>
          <label class="zf-switch" for="productToggle" title="Search and add products to this return">
            <input class="form-check-input" type="checkbox" [(ngModel)]="enableProductSearch" id="productToggle"
              [disabled]="viewOnly" />
            <span>Add manually</span>
          </label>
          @if (!viewOnly) {
          <button type="button" class="btn btn-secondary" (click)="removeSelectedItems()" [disabled]="!selectedCount">
            <i class="bi bi-trash3"></i> Delete selected ({{ selectedCount }})
          </button>
          <button type="button" class="btn btn-secondary" (click)="clearPrnLines()" [disabled]="!prnItems.length">
            Clear lines
          </button>
          }
        </div>
      </div>

      <!-- Product search + add-line strip -->
      @if (enableProductSearch) {
      <div class="zf-add-strip prn-add-strip">
        <div class="zf-tool" style="width:190px">
          <span class="zf-tool-k mono">{{ selectedSearchType.toUpperCase() }}</span>
          <ng-select [items]="searchResults" bindLabel="sku" [typeahead]="productCodeSearch$"
            [(ngModel)]="selectedProduct" [ngModelOptions]="{ standalone: true }"
            (change)="onSearchResultSelected($event); populatePriceOptions($event)"
            (change)="populatePriceOptions(selectedProduct)" [loading]="isCodeSearching"
            [placeholder]="'Type ' + selectedSearchType.toUpperCase() + ' to search'" [virtualScroll]="true"
            [clearable]="true">
            <ng-template ng-option-tmp let-item="item">
              <div class="ss-row">
                <span class="ss-row__primary"><span class="zf-mono">{{ item.sku }}</span> · {{ item.longDescription }}</span>
                <span class="ss-row__secondary">
                  @if (item.plu) { PLU {{ item.plu }} }
                  @if (item.plu && item.ean) { · }
                  @if (item.ean) { EAN {{ item.ean }} }
                </span>
              </div>
            </ng-template>
          </ng-select>
        </div>

        <div class="zf-tool" style="width:170px">
          <span class="zf-tool-k mono">EAN</span>
          <ng-select [items]="eanSearchResults" bindLabel="matchedEan" [typeahead]="productEanSearch$"
            [(ngModel)]="selectedProduct" [ngModelOptions]="{ standalone: true }"
            (change)="onSearchResultSelected($event); populatePriceOptions($event)" [loading]="isEanSearching"
            placeholder="Type / scan EAN" [virtualScroll]="true" [clearable]="true">
            <ng-template ng-label-tmp let-item="item">{{ item.matchedEan || item.ean || item.sku }}</ng-template>
            <ng-template ng-option-tmp let-item="item">
              <div class="ss-row">
                <span class="ss-row__primary"><span class="zf-mono">{{ item.matchedEan }}</span> · {{ item.longDescription }}</span>
                <span class="ss-row__secondary">{{ item.sku }}</span>
              </div>
            </ng-template>
          </ng-select>
        </div>

        <div class="zf-tool" style="width:260px">
          <span class="zf-tool-k"><i class="bi bi-plus-lg"></i></span>
          <ng-select [items]="products" bindLabel="longDescription" [typeahead]="productSearch$"
            [(ngModel)]="selectedProduct" (change)="onProductSelected($event)" [loading]="isSearching"
            placeholder="Type to search product" [virtualScroll]="true" [searchFn]="customSearchFn">
            <ng-template ng-label-tmp let-item="item">{{ item.longDescription || item.skuCode }}</ng-template>
            <ng-template ng-option-tmp let-item="item">
              <div class="ss-row">
                <span class="ss-row__primary">{{ item.longDescription || item.skuCode }}</span>
                <span class="ss-row__secondary">{{ item.sku || item.skuCode }}</span>
              </div>
            </ng-template>
          </ng-select>
        </div>

        @if (selectedProduct) {
        <div class="zf-field w-90">
          <span class="zf-flabel">Qty</span>
          <input #quantityInput type="number" class="form-control" [(ngModel)]="selectedQuantity" appQtyInput
            [allowDecimal]="allowsDecimal(selectedProduct)" />
        </div>
        <div class="zf-field">
          <span class="zf-flabel">Cost price</span>
          <select class="form-select" [(ngModel)]="enteredCostPrice" (ngModelChange)="onCostPriceSelected($event)"
            [ngModelOptions]="{ standalone: true }" (keydown.enter)="confirmAddToTable()" [disabled]="viewOnly">
            <option value="" disabled>Select Cost Price</option>
            @for (price of allCostPrices; track price) {
            <option [value]="price">{{ price }}</option>
            }
          </select>
        </div>
        <div class="zf-field">
          <span class="zf-flabel">MRP</span>
          <select class="form-select" [(ngModel)]="selectedMRP" (ngModelChange)="onMRPSelected($event)"
            [ngModelOptions]="{ standalone: true }" (keydown.enter)="confirmAddToTable()" [disabled]="viewOnly">
            <option value="" disabled>Select MRP</option>
            @for (mrp of allMRPs; track mrp) {
            <option [value]="mrp">{{ mrp }}{{ mrp === currentMRP ? " (current)" : "" }}</option>
            }
          </select>
        </div>
        <button type="button" class="btn btnprimary" (click)="confirmAddToTable()">Add line</button>
        }
      </div>
      }

      @if (selectedVendorName && !viewOnly) {
      <div class="zf-scoped">
        <span class="k">Scoped</span>
        <span>Only SKUs supplied by <strong>{{ selectedVendorName }}</strong> can be returned · quantities are capped at stock on hand</span>
        <span class="hint">#, SKU and description stay locked left · scroll right for tax and discounts</span>
      </div>
      }

      <!-- Rows the uploaded file could not resolve — shown instead of being
           dropped silently, so the user can fix the sheet and re-upload. -->
      @if (!viewOnly && uploadErrors.length > 0) {
      <div class="zf-issues">
        <div class="zf-issues-head">
          <i class="bi bi-exclamation-triangle-fill"></i>
          {{ uploadErrors.length }} row(s) from the uploaded file were skipped
          <button type="button" class="zf-link ms-auto" (click)="uploadErrors = []">Dismiss</button>
        </div>
        <div style="max-height: 220px; overflow-y: auto;">
          <table class="table mb-0">
            <thead>
              <tr>
                <th style="width: 90px">Excel row</th>
                <th style="width: 180px">SKU</th>
                <th>Reason</th>
              </tr>
            </thead>
            <tbody>
              @for (e of uploadErrors; track $index) {
              <tr>
                <td class="zf-mono">{{ e.row }}</td>
                <td><code>{{ e.code || '-' }}</code></td>
                <td>{{ e.error }}</td>
              </tr>
              }
            </tbody>
          </table>
        </div>
      </div>
      }

      @if (prnItems.length) {
      <div class="zf-sheet-wrap prn-sheet-wrap">
        <table class="zf-sheet prn-sheet">
          <thead>
            <tr>
              <th class="st ctr" style="--l:0; width:36px">
                <input type="checkbox" class="form-check-input prn-check" [checked]="allItemsSelected"
                  (change)="toggleSelectAll($any($event.target).checked)" [disabled]="viewOnly"
                  aria-label="Select all items" />
              </th>
              <th class="st" style="--l:36px; width:44px">#</th>
              <th class="st" style="--l:80px; width:96px">SKU code</th>
              <th class="st st-end" style="--l:176px; min-width:250px">Description</th>
              <th style="width:84px">PLU code</th>
              <th style="width:122px">EAN code</th>
              <th class="num" style="width:78px">Inner qty</th>
              <th style="width:64px">UOM</th>
              <th class="num" style="width:88px">Return qty</th>
              <th class="num" style="width:78px">Free qty</th>
              <th class="num" style="width:104px">Basic cost price</th>
              <th style="width:110px">Cost price</th>
              <th class="num" style="width:66px">GST %</th>
              <th class="num" style="width:70px">SGST %</th>
              <th class="num" style="width:70px">CGST %</th>
              <th class="num" style="width:86px">GST amt</th>
              <th style="width:104px">MRP amt</th>
              <th class="num" style="width:96px">Selling price</th>
              <th class="num" style="width:84px">Freight</th>
              <th class="num" style="width:104px">Total value</th>
              <th class="num" style="width:76px">Disc 1 %</th>
              <th class="num" style="width:90px">Disc 1 amt</th>
              <th class="num" style="width:76px">Disc 2 %</th>
              <th class="num" style="width:90px">Disc 2 amt</th>
              <th class="num" style="width:92px">Cash disc %</th>
              <th class="num" style="width:100px">Cash disc amt</th>
              <th class="num" style="width:96px">Scheme disc</th>
              <th class="num" style="width:90px">Fixed disc</th>
              <th class="num" style="width:100px">Overall disc</th>
              <th style="width:88px">HSN code</th>
              <th class="num" style="width:72px">CESS %</th>
              <th class="num" style="width:86px">CESS amt</th>
              <th class="ctr" style="width:62px">Action</th>
            </tr>
          </thead>
          <tbody>
            @for (row of visiblePrnItems; track row.item; let i = $index) {
            <tr [class.is-selected]="isItemSelected(row.item)">
              <td class="st ctr" style="--l:0">
                <input type="checkbox" class="form-check-input prn-check" [checked]="isItemSelected(row.item)"
                  (change)="toggleItemSelection(row.item)" [disabled]="viewOnly"
                  [attr.aria-label]="'Select ' + row.item.skuCode" />
              </td>
              <td class="st zf-idx" style="--l:36px">{{ row.index + 1 }}</td>
              <td class="st" style="--l:80px"><span class="zf-sku">{{ row.item.skuCode }}</span></td>
              <td class="st st-end zf-name" style="--l:176px">
                <span class="n" [title]="row.item.description">{{ row.item.description }}</span>
                <span class="s">{{ row.item.uom || '—' }}{{ row.item.hsnCode ? ' · HSN ' + row.item.hsnCode : '' }}</span>
              </td>
              <td class="dim">{{ row.item.pluCode || "-" }}</td>
              <td class="dim">{{ row.item.eanCode }}</td>
              <td class="num ink3">{{ row.item.innerQuantity }}</td>
              <td class="dim prn-uom">{{ row.item.uom }}</td>
              <td class="num">
                <input type="number" class="zf-cell-in" style="width:64px" [id]="'qty-' + row.index"
                  [(ngModel)]="row.item.returnedQuantity" appQtyInput [allowDecimal]="allowsDecimal(row.item)"
                  (change)="updatePrnSummary()" (change)="onReturnedQuantityChange(row.item)" [readOnly]="viewOnly"
                  min="0" [max]="row.item.soh || row.item.productVariant?.soh || 0" />
              </td>
              <td class="num">
                <input type="number" class="zf-cell-in w-sm" appQtyInput [allowDecimal]="allowsDecimal(row.item)"
                  [(ngModel)]="row.item.freeQuantity" [readOnly]="viewOnly" />
              </td>
              <td class="num">{{ row.item.actualCostPrice }}</td>
              <td>
                <select class="zf-price-sel" [(ngModel)]="row.item.costPrice"
                  (ngModelChange)="onTableCostPriceChange(row.item, $event)" [ngModelOptions]="{ standalone: true }"
                  [disabled]="viewOnly">
                  <option value="" disabled>Select Cost Price</option>
                  @if (!row.item?.productVariant?.pricing) {
                  <option [value]="row.item.costPrice" selected>{{ row.item.costPrice }}</option>
                  }
                  @for (price of row.item?.productVariant?.pricing; track price) {
                  <option [value]="price.costPrice">{{ price.costPrice }}</option>
                  }
                </select>
              </td>
              <td class="num ink3">{{ row.item.gstPercent }}</td>
              <td class="num dim">{{ row.item.gstPercent / 2 }}</td>
              <td class="num dim">{{ row.item.gstPercent / 2 }}</td>
              <td class="num ink3">{{ row.item.gstAmount | number : "1.2-2" }}</td>
              <td>
                <select class="zf-price-sel" [(ngModel)]="row.item.mrp"
                  (ngModelChange)="onTableMRPChange(row.item, $event)" [ngModelOptions]="{ standalone: true }"
                  [disabled]="viewOnly">
                  <option value="" disabled>Select MRP</option>
                  @if (!row.item?.productVariant?.pricing) {
                  <option [value]="row.item.mrp" selected>{{ row.item.mrp }}</option>
                  }
                  @for (price of row.item?.productVariant?.pricing; track price) {
                  <option [value]="price.mrp">{{ price.mrp }}</option>
                  }
                </select>
              </td>
              <td class="num">
                <input type="number" class="zf-cell-in" style="width:76px" [(ngModel)]="row.item.sellingPrice"
                  [readOnly]="viewOnly" />
              </td>
              <td class="num">
                <input type="number" class="zf-cell-in" [(ngModel)]="row.item.freightCharges"
                  (change)="onPriceChange(row.item, 'freightCharges')" [readOnly]="viewOnly" />
              </td>
              <td class="num strong">{{ row.item.totalPrice | number : "1.2-2" }}</td>
              <td class="num">
                <input type="number" class="zf-cell-in w-sm" [(ngModel)]="row.item.discountOnePercent"
                  (ngModelChange)="onPriceChange(row.item, 'discountOnePercent')" placeholder="%" [readOnly]="viewOnly" />
              </td>
              <td class="num">
                <input type="number" class="zf-cell-in" [(ngModel)]="row.item.discountOneAmount"
                  (ngModelChange)="onPriceChange(row.item, 'discountOneAmount')" placeholder="₹" [readOnly]="viewOnly" />
              </td>
              <td class="num">
                <input type="number" class="zf-cell-in w-sm" [(ngModel)]="row.item.discountTwoPercent"
                  (ngModelChange)="onPriceChange(row.item, 'discountTwoPercent')" placeholder="%" [readOnly]="viewOnly" />
              </td>
              <td class="num">
                <input type="number" class="zf-cell-in" [(ngModel)]="row.item.discountTwoAmount"
                  (ngModelChange)="onPriceChange(row.item, 'discountTwoAmount')" placeholder="₹" [readOnly]="viewOnly" />
              </td>
              <td class="num">
                <input type="number" class="zf-cell-in w-sm" [(ngModel)]="row.item.cashDiscountPercent"
                  (ngModelChange)="onPriceChange(row.item, 'cashDiscountPercent')" placeholder="%" [readOnly]="viewOnly" />
              </td>
              <td class="num">
                <input type="number" class="zf-cell-in" [(ngModel)]="row.item.cashDiscountAmount"
                  (ngModelChange)="onPriceChange(row.item, 'cashDiscountAmount')" placeholder="₹" [readOnly]="viewOnly" />
              </td>
              <td class="num">
                <input type="number" class="zf-cell-in" [(ngModel)]="row.item.schemeDiscountAmount"
                  (change)="onPriceChange(row.item, 'schemeDiscountAmount')" [readOnly]="viewOnly" />
              </td>
              <td class="num">
                <input type="number" class="zf-cell-in" [(ngModel)]="row.item.fixedDiscountAmount"
                  (change)="onPriceChange(row.item, 'fixedDiscountAmount')" [readOnly]="viewOnly" />
              </td>
              <td class="num">
                <input type="number" class="zf-cell-in" [(ngModel)]="row.item.overallDiscountAmount"
                  (change)="onPriceChange(row.item, 'overallDiscountAmount')" [readOnly]="viewOnly" />
              </td>
              <td class="ink3">{{ row.item.hsnCode }}</td>
              <td class="num">
                <input type="number" class="zf-cell-in w-sm" [(ngModel)]="row.item.cessRate" />
              </td>
              <td class="num ink3">{{ row.item.cessAmount | number : "1.2-2" }}</td>
              <td class="ctr">
                <button type="button" class="zf-del" title="Remove line" (click)="removePrnLine(row.item)"
                  [disabled]="viewOnly">
                  <i class="bi bi-trash3"></i>
                </button>
              </td>
            </tr>
            }
            @if (!visiblePrnItems.length) {
            <tr>
              <td colspan="33" class="zf-sheet-empty">
                <div>
                  <span class="t">No lines match this filter</span>
                  <span class="s">Clear the filters to see all {{ prnItems.length }} lines.</span>
                </div>
              </td>
            </tr>
            }
          </tbody>
          <tfoot>
            <tr>
              <td class="st" colspan="4" style="--l:0"><span class="k">TOTAL</span>{{ prnLineCountLabel }}</td>
              <td></td><td></td><td></td><td></td>
              <td class="num">{{ prnSummary.returnedQuantity | number:'1.0-2' }}</td>
              <td class="num dim">{{ totalFreeQty | number:'1.0-2' }}</td>
              <td></td><td></td><td></td><td></td><td></td>
              <td class="num dim">{{ prnSummary.gstAmount | number:'1.2-2' }}</td>
              <td></td><td></td><td></td>
              <td class="num strong">{{ totalLineValue | number:'1.2-2' }}</td>
              <td></td><td></td><td></td><td></td><td></td><td></td>
              <td class="num dim">{{ prnSummary.schemeDiscountAmount | number:'1.2-2' }}</td>
              <td class="num dim">{{ prnSummary.fixedDiscountAmount | number:'1.2-2' }}</td>
              <td class="num dim">{{ discountTotal | number:'1.2-2' }}</td>
              <td></td><td></td>
              <td class="num dim">{{ prnSummary.cessAmount | number:'1.2-2' }}</td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>
      } @else {
      <div class="zf-sheet-blank">
        <span class="t">No lines on this return yet</span>
        <span class="s">
          @if (viewOnly) { This return has no lines. }
          @else { Search a product above, or upload the filled template to add lines in bulk. }
        </span>
      </div>
      }
    </div>

    <!-- ── Return value strip ─────────────────────────────────────────── -->
    <div class="zf-order-strip">
      <div class="zf-strip-info">
        <div class="zf-strip-line">
          <span class="dot" [class.ok]="prnItems.length"></span>
          {{ totalProducts }} item{{ totalProducts === 1 ? '' : 's' }} · {{ totalReturnedQuantity | number:'1.0-2' }} units
          <span class="zf-units">(return + free qty)</span>
        </div>
        <div class="zf-strip-sub">
          @if (!viewOnly && prnItems.length === 0) {
          <span>Please add at least one product to submit or save.</span>
          } @else {
          <span>Scheme − ₹{{ prnSummary.schemeDiscountAmount | number:'1.2-2' }}</span>
          <span class="sep">·</span>
          <span>Fixed − ₹{{ prnSummary.fixedDiscountAmount | number:'1.2-2' }}</span>
          <span class="sep">·</span>
          <span>Other discounts − ₹{{ (prnSummary.otherDiscounts || otherDiscounts) | number:'1.2-2' }}</span>
          <span class="sep">·</span>
          <span>Freight ₹{{ prnSummary.freightCharges | number:'1.2-2' }}</span>
          <span class="sep">·</span>
          <span>Packing ₹{{ prnSummary.packingCharges | number:'1.2-2' }}</span>
          <span class="sep">·</span>
          <span>Other charges ₹{{ prnSummary.otherCharges | number:'1.2-2' }}</span>
          }
        </div>
      </div>
      <div class="zf-strip-kpis">
        <div class="zf-kpi"><span class="k">Sub total</span><span class="v">₹{{ prnSummary.subTotal | number:'1.2-2' }}</span></div>
        <div class="zf-kpi green"><span class="k">Discount</span><span class="v">− ₹{{ discountTotal | number:'1.2-2' }}</span></div>
        <div class="zf-kpi"><span class="k">GST</span><span class="v">₹{{ prnSummary.gstAmount | number:'1.2-2' }}</span></div>
        @if (prnSummary.cessAmount) {
        <div class="zf-kpi"><span class="k">Cess</span><span class="v">₹{{ prnSummary.cessAmount | number:'1.2-2' }}</span></div>
        }
        <div class="zf-kpi"><span class="k">Charges</span><span class="v">₹{{ chargesTotal | number:'1.2-2' }}</span></div>
        <div class="zf-kpi prn-roundoff">
          <span class="k">Round off</span>
          <input type="number" class="zf-cell-in" [(ngModel)]="prnSummary.roundOff" (ngModelChange)="onRoundOffChanged()"
            placeholder="₹" />
        </div>
        <div class="zf-kpi dark">
          <span class="k">Net return value</span>
          <span class="v">₹{{ prnSummary.finalTotal | number:'1.2-2' }}</span>
        </div>
      </div>
    </div>
  </div>
</div>
